import { customFetcher, HttpError } from "./http";
import { getAuthorizedRequestConfig } from "./auth";

export type WalletNetwork = "MAINNET" | "TESTNET";
export type WalletVersion =
  | "V3R2"
  | "V4R2"
  | "HIGHLOAD_V2"
  | "SAFE_MULTISIG"
  | "OTHER";

export type WalletResponse = {
  id: string;
  network: WalletNetwork;
  workchain: number;
  rawAddress: string;
  friendlyAddress: string;
  walletVersion: WalletVersion;
  publicKeyHex: string | null;
  balance: string;
  isDeployed: boolean;
  lastSyncSeqno: number | null;
  lastSyncedAt: string | null;
  isVerified: boolean;
  verifiedAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateWalletRequest = {
  network: WalletNetwork;
  address: string;
  walletVersion: WalletVersion;
  publicKeyHex: string;
  stateInitB64?: string;
  label?: string;
  isDeployed?: boolean;
  metadata?: Record<string, unknown>;
};

export type TonProofChallengeResponse = {
  challengeId: string;
  payload: string;
  domain: {
    value: string;
    lengthBytes: number;
  };
  ttl: number;
  createdAt: number;
  expiresAt: number;
};

export type TonProofDomain = {
  value: string;
  lengthBytes: number;
};

export type TonProofPayload = {
  payload: string;
  signature: string;
  timestamp: number;
  domain: TonProofDomain;
};

export type TonProofRequest = {
  address: string;
  chain: number;
  state_init?: string;
  proof: TonProofPayload;
};

export type VerifyTonProofRequest = {
  challengeId: string;
  tonProof: TonProofRequest;
  extra?: Record<string, unknown>;
};

const getConfig = () => getAuthorizedRequestConfig();

export const listWallets = async (): Promise<WalletResponse[]> => {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    // Include auth/storage waits and token refresh in the deadline. Aborting
    // fetch alone would leave those earlier steps pending indefinitely.
    return await new Promise<WalletResponse[]>((resolve, reject) => {
      timeout = setTimeout(() => {
        reject(new HttpError('Wallet lookup timeout', 408));
        controller.abort();
      }, 20_000);
      void customFetcher<WalletResponse[]>('/wallets', {
        ...getConfig(),
        signal: controller.signal,
      }).then(resolve, reject);
    });
  } finally {
    clearTimeout(timeout);
  }
};

export const getActiveWallet = async () =>
  customFetcher<WalletResponse>("/wallets/active", getConfig());

export const createWallet = async (data: CreateWalletRequest) =>
  customFetcher<WalletResponse>(
    {
      url: "/wallets",
      method: "POST",
      data,
    },
    getConfig(),
  );

export const syncWallet = async (walletId: string) =>
  customFetcher<WalletResponse>(
    {
      url: `/wallets/${walletId}/sync`,
      method: "POST",
    },
    getConfig(),
  );

export const deleteWallet = async (walletId: string) =>
  customFetcher<void>(
    {
      url: `/wallets/${walletId}`,
      method: "DELETE",
    },
    getConfig(),
  );

export const requestGenericTonProofChallenge = async () =>
  customFetcher<TonProofChallengeResponse>(
    {
      url: `/wallets/proof/challenge`,
      method: "POST",
    },
    getConfig(),
  );

export const requestTonProofChallenge = async (walletId: string) =>
  customFetcher<TonProofChallengeResponse>(
    {
      url: `/wallets/${walletId}/proof/challenge`,
      method: "POST",
    },
    getConfig(),
  );

export const verifyTonProof = async (
  walletId: string,
  data: VerifyTonProofRequest,
) =>
  customFetcher<WalletResponse>(
    {
      url: `/wallets/${walletId}/proof/verify`,
      method: "POST",
      data,
    },
    getConfig(),
  );
